"use client";

import React, { useCallback, useMemo, useState } from "react";
import Select from "react-select";
import { useField } from "formik";
import { usePathname } from "next/navigation";
import styles from "@/components/search/SearchModal.module.css";
import { loading } from "@/lib/features/hotel/hotelSlice";
import { useAppSelector } from "@/lib/hooks";

interface Room {
  adults: number;
  children_ages: (number | null)[];
}

interface GuestSearchProps {
  name: string;
  guests: Room[];
  setFieldValue: (name: string, value: any) => void;
}

/* ---------------- Counter ---------------- */

const Counter: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}> = ({ label, value, onChange, min = 0, max = 10 }) => (
  <div className="row y-gap-10 justify-between items-center">
    <div className="col-auto">
      <div className="text-14 sm:text-12 xs:text-10 lh-12 fw-500">{label}</div>
    </div>
    <div className="col-auto">
      <div className="d-flex items-center">
        <button
          type="button"
          className="button -outline-blue-1 text-blue-1 size-38 md:size-20 rounded-4"
          onClick={() => onChange(Math.max(value - 1, min))}
        >
          <i className="icon-minus text-12" />
        </button>

        <div className="flex-center size-20 ml-5 mr-5">
          <div className="text-14 sm:text-12 xs:text-10">{value}</div>
        </div>

        <button
          type="button"
          className="button -outline-blue-1 text-blue-1 size-38 md:size-20 rounded-4"
          onClick={() => onChange(Math.min(value + 1, max))}
        >
          <i className="icon-plus text-12" />
        </button>
      </div>
    </div>
  </div>
);

/* ---------------- GuestSearch ---------------- */

const GuestSearch: React.FC<GuestSearchProps> = ({
  name,
  guests,
  setFieldValue,
}) => {
  const hotelLoading = useAppSelector(loading);
  const [, meta] = useField(name);
  const currentPage = usePathname();

  const isHotelPage = false;

  /* -------- Handle room updates -------- */

  const handleRoomChange = useCallback(
    (index: number, key: keyof Room, value: any) => {
      const updatedGuests = guests.map((guest, i) => {
        if (i !== index) return guest;

        return {
          ...guest,
          [key]: value,
        };
      });

      setFieldValue("guests", updatedGuests);
    },
    [guests, setFieldValue]
  );

  /* -------- Room add / remove -------- */

  const addRoom = () => {
    setFieldValue("guests", [...guests, { adults: 1, children_ages: [] }]);
  };

  const removeRoom = (index: number) => {
    setFieldValue(
      "guests",
      guests.filter((_, i) => i !== index)
    );
  };

  /* -------- Child age options -------- */

  const createOptions = Array.from({ length: 18 }, (_, i) => ({
    value: i,
    label: i === 0 ? "Under 1" : `${i}`,
  }));

  /* -------- Totals -------- */

  const { totalAdults, totalChildren } = useMemo(() => {
    return {
      totalAdults: guests.reduce((sum, r) => sum + r.adults, 0),
      totalChildren: guests.reduce(
        (sum, r) => sum + r.children_ages.length,
        0
      ),
    };
  }, [guests]);

  /* ---------------- Render ---------------- */

  return (
    <div
      className={
        currentPage === "/hotel-single-v1"
          ? "searchMenu-guests px-20 py-10 border-light rounded-4 js-form-dd js-form-counters"
          : "searchMenu-guests px-30 sm:py-10 sm:mb-20 lg:py-20 lg:px-0 js-form-dd js-form-counters position-relative"
      }
    >
      <div
        data-bs-toggle={isHotelPage ? undefined : "dropdown"}
        data-bs-auto-close="outside"
        tabIndex={0}
      >
        <h4 className="text-15 fw-500 ls-2 lh-16">Guest</h4>

        <div className="text-15 text-light-1 ls-2 lh-16">
          {guests.length} {guests.length > 1 ? "rooms" : "room"} –{" "}
          {totalAdults} {totalAdults > 1 ? "adults" : "adult"} –{" "}
          {totalChildren} {totalChildren > 1 ? "children" : "child"}
        </div>
      </div>

      {!isHotelPage && (
        <div
          className={`${styles.dropdownMenu} shadow-2 dropdown-menu min-width-dropdown-400`}
        >
          <div className="bg-white px-30 py-30 sm:px-15 sm:py-15 rounded-4 counter-box">
            {guests.map((room, roomIndex) => (
              <div key={roomIndex} className="mb-10">
                <h4 className="text-14 sm:text-12 xs:text-10 fw-500">
                  Room {roomIndex + 1}
                </h4>

                <Counter
                  label="Adults"
                  value={room.adults}
                  min={1}
                  max={9}
                  onChange={value =>
                    handleRoomChange(roomIndex, "adults", value)
                  }
                />

                <Counter
                  label="Children (ages 0 to 17)"
                  value={room.children_ages.length}
                  min={0}
                  max={8}
                  onChange={value =>
                    handleRoomChange(
                      roomIndex,
                      "children_ages",
                      value > room.children_ages.length
                        ? [...room.children_ages, 1]
                        : room.children_ages.slice(0, -1)
                    )
                  }
                />

                <div className="row y-gap-10">
                  {room.children_ages.map((child, childIndex) => (
                    <React.Fragment key={childIndex}>
                      <div className="col-auto text-15 sm:text-12 xs:text-10">
                        Child {childIndex + 1} Age:
                      </div>

                      <div className="col-md-6 col-sm-2">
                        <Select
                          classNamePrefix="react-select"
                          options={createOptions}
                          value={{
                            value: child,
                            label: child === 0 ? "Under 1" : `${child}`,
                          }}
                          onChange={option => {
                            const updated = [...room.children_ages];
                            updated[childIndex] = option?.value as number;

                            handleRoomChange(
                              roomIndex,
                              "children_ages",
                              updated
                            );
                          }}
                        />
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {roomIndex > 0 && (
                  <div className="d-flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeRoom(roomIndex)}
                      className="mt-10 py-10 button -outline-red-1 text-red-1 w-50 sm:text-12"
                    >
                      Remove room
                    </button>
                  </div>
                )}

                <hr className="border-dark my-20" />
              </div>
            ))}

            {guests.length <= 19 && (
              <div className={`${styles.addRoomContainer} d-flex justify-end`}>
                <button
                  type="button"
                  onClick={addRoom}
                  className="button py-10 -dark-1 bg-blue-1 text-white w-50 sm:text-10"
                >
                  Add another room
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestSearch;
